import React, { useMemo } from "react";

/**
 * Performance-metrics panel for the Live Training Progress modal.
 *
 * The keys it receives are exactly what the training pipeline persists on a
 * version's `performanceMetrics`:
 *
 *   classification            -> top1_acc / top5_acc / train_loss
 *                                (b_training.py `train_yolo`)
 *   detection & segmentation  -> ultralytics `results_dict`, i.e.
 *                                `metrics/<name>(B)` for box, `(M)` for mask,
 *                                plus `fitness`
 *
 * Those raw keys are unreadable on screen (`metrics/mAP50-95(B)`), carry no
 * unit, and mix ratios with losses — so every key is looked up in the catalogue
 * below for a human label, an icon, whether it is a 0..1 ratio or a loss, and
 * how important it is. An unknown key is NOT dropped: `describeMetric` falls
 * back to parsing the ultralytics shape, so a future metric renders as a
 * readable tile rather than disappearing from the panel.
 */

// Lower rank = more important. The lowest-ranked metric present becomes the
// headline tile, which is why every catalogue entry carries one.
const METRIC_CATALOG = {
  // ── detection / segmentation (ultralytics results_dict) ────────────────
  "metrics/map50-95(b)": {
    label: "mAP@50-95",
    scope: "Box",
    icon: "fa-award",
    kind: "ratio",
    accent: "var(--chart-1)",
    rank: 1,
    hint: "Mean average precision averaged over IoU 0.50:0.95 — the headline detection score.",
  },
  "metrics/map50(b)": {
    label: "mAP@50",
    scope: "Box",
    icon: "fa-bullseye",
    kind: "ratio",
    accent: "var(--chart-2)",
    rank: 2,
    hint: "Mean average precision at IoU 0.50.",
  },
  "metrics/precision(b)": {
    label: "Precision",
    scope: "Box",
    icon: "fa-crosshairs",
    kind: "ratio",
    accent: "var(--chart-3)",
    rank: 3,
    hint: "Of everything the model detected, the share that was correct.",
  },
  "metrics/recall(b)": {
    label: "Recall",
    scope: "Box",
    icon: "fa-search",
    kind: "ratio",
    accent: "var(--chart-5)",
    rank: 4,
    hint: "Of everything that should have been detected, the share the model found.",
  },
  "metrics/map50-95(m)": {
    label: "mAP@50-95",
    scope: "Mask",
    icon: "fa-award",
    kind: "ratio",
    accent: "var(--chart-1)",
    rank: 11,
    hint: "Mask mean average precision averaged over IoU 0.50:0.95.",
  },
  "metrics/map50(m)": {
    label: "mAP@50",
    scope: "Mask",
    icon: "fa-draw-polygon",
    kind: "ratio",
    accent: "var(--chart-2)",
    rank: 12,
    hint: "Mask mean average precision at IoU 0.50.",
  },
  "metrics/precision(m)": {
    label: "Precision",
    scope: "Mask",
    icon: "fa-crosshairs",
    kind: "ratio",
    accent: "var(--chart-3)",
    rank: 13,
    hint: "Mask precision.",
  },
  "metrics/recall(m)": {
    label: "Recall",
    scope: "Mask",
    icon: "fa-search",
    kind: "ratio",
    accent: "var(--chart-5)",
    rank: 14,
    hint: "Mask recall.",
  },
  fitness: {
    label: "Fitness",
    icon: "fa-medal",
    kind: "ratio",
    accent: "var(--chart-3)",
    rank: 20,
    hint: "Ultralytics' weighted blend of the mAP scores — what early stopping tracks.",
  },

  // ── classification ─────────────────────────────────────────────────────
  top1_acc: {
    label: "Top-1 Accuracy",
    icon: "fa-bullseye",
    kind: "ratio",
    accent: "var(--chart-1)",
    rank: 1,
    hint: "Share of images whose single best prediction was the correct class.",
  },
  top5_acc: {
    label: "Top-5 Accuracy",
    icon: "fa-layer-group",
    kind: "ratio",
    accent: "var(--chart-2)",
    rank: 2,
    hint: "Share of images where the correct class was in the model's top five guesses.",
  },
  train_loss: {
    label: "Train Loss",
    icon: "fa-chart-line",
    kind: "loss",
    accent: "var(--chart-4)",
    rank: 30,
    hint: "Final training loss. Lower is better; it has no upper bound.",
  },
};

const humanize = (raw) =>
  String(raw)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Resolve a raw metric key to how it should be presented. Catalogue first,
 * then a parse of the `metrics/<name>(B|M)` shape so unlisted ultralytics keys
 * still read sensibly.
 */
const describeMetric = (rawKey) => {
  const key = String(rawKey);
  const known = METRIC_CATALOG[key.toLowerCase()];
  if (known) return { ...known, key };

  const parsed = key.match(/^metrics\/(.+?)(?:\((B|M)\))?$/i);
  const bare = parsed ? parsed[1] : key;
  const scopeCode = parsed && parsed[2] ? parsed[2].toUpperCase() : null;
  const isLoss = /loss/i.test(bare);

  return {
    key,
    label: humanize(bare),
    scope: scopeCode ? (scopeCode === "M" ? "Mask" : "Box") : null,
    icon: isLoss ? "fa-chart-line" : "fa-chart-area",
    kind: isLoss ? "loss" : "auto",
    accent: "var(--chart-3)",
    rank: 90,
  };
};

const trimNumber = (value) => {
  const fixed = value.toFixed(4);
  return fixed.replace(/\.?0+$/, "") || "0";
};

/**
 * Turn one raw entry into everything the tile needs to render. `pct` is only
 * non-null for values that genuinely sit on a 0..1 scale, so a loss never gets
 * a meter bar implying it is 12% of something.
 */
const buildMetric = ([rawKey, rawValue]) => {
  const meta = describeMetric(rawKey);

  if (typeof rawValue !== "number" || !Number.isFinite(rawValue)) {
    return {
      ...meta,
      display: rawValue === null || rawValue === undefined ? "-" : String(rawValue),
      unit: null,
      pct: null,
      note: null,
    };
  }

  const isRatio =
    meta.kind === "ratio" || (meta.kind === "auto" && rawValue >= 0 && rawValue <= 1);

  if (isRatio) {
    return {
      ...meta,
      display: (rawValue * 100).toFixed(1),
      unit: "%",
      pct: Math.max(0, Math.min(100, rawValue * 100)),
      note: null,
    };
  }

  return {
    ...meta,
    display: trimNumber(rawValue),
    unit: null,
    pct: null,
    note: meta.kind === "loss" ? "lower is better" : null,
  };
};

const MetricMeter = ({ pct }) => (
  <div
    className="lf-metrics__meter"
    role="progressbar"
    aria-valuenow={Math.round(pct)}
    aria-valuemin={0}
    aria-valuemax={100}
  >
    <span className="lf-metrics__meter-fill" style={{ width: `${pct}%` }} />
  </div>
);

const MetricLabel = ({ metric }) => (
  <span className="lf-metrics__label">
    <i className={`fa ${metric.icon} lf-metrics__label-icon`} aria-hidden />
    {metric.label}
    {metric.scope && <span className="lf-metrics__scope">{metric.scope}</span>}
  </span>
);

/**
 * The empty state is three different messages, not one. A run that has not
 * finished has no metrics *yet* — the pipeline only writes `performanceMetrics`
 * once training completes — and saying "no metrics reported" there reads as a
 * broken model rather than a run still in flight.
 */
const MetricsEmptyState = ({ status }) => {
  const normalized = (status || "").toString().toLowerCase().replace(/[\s_-]/g, "");
  // "pending" is deliberately NOT here: it is the badge's default for a version
  // nobody has trained yet, which is not a run in flight.
  const running = ["queued", "inprogress", "training", "running"].includes(normalized);
  const failed = ["failed", "error", "cancelled", "canceled"].includes(normalized);

  const tone = running ? "info" : failed ? "danger" : "muted";
  const icon = running ? "fa-hourglass-half" : failed ? "fa-exclamation-triangle" : "fa-info-circle";
  const message = running
    ? "Metrics are published when the run completes — the log below carries live progress."
    : failed
      ? "This run ended before it reported any metrics."
      : "No metrics reported for this version.";

  return (
    <div className={`lf-metrics__empty lf-metrics__empty--${tone}`}>
      <i className={`fa ${icon}`} aria-hidden />
      <span>{message}</span>
    </div>
  );
};

const TrainingMetricsPanel = ({ metrics, status }) => {
  const entries = useMemo(() => {
    if (!metrics || typeof metrics !== "object" || Array.isArray(metrics)) return [];
    return Object.entries(metrics)
      .map(buildMetric)
      .sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
  }, [metrics]);

  const [hero, ...rest] = entries;

  return (
    <section className="lf-metrics">
      <header className="lf-metrics__head">
        <h6 className="lf-metrics__title">
          <i className="fa fa-chart-area" aria-hidden /> Performance Metrics
        </h6>
        {entries.length > 0 && (
          <span className="lf-metrics__count">
            {entries.length} {entries.length === 1 ? "metric" : "metrics"}
          </span>
        )}
      </header>

      {entries.length === 0 ? (
        <MetricsEmptyState status={status} />
      ) : (
        <>
          <div
            className={`lf-metrics__hero${hero.pct === null ? " lf-metrics__hero--solo" : ""}`}
            style={{ "--lf-metric-accent": hero.accent }}
          >
            <div className="lf-metrics__hero-info">
              <MetricLabel metric={hero} />
              <div className="lf-metrics__hero-value" title={hero.hint || undefined}>
                {hero.display}
                {hero.unit && <span className="lf-metrics__unit">{hero.unit}</span>}
              </div>
              {hero.note && <span className="lf-metrics__note">{hero.note}</span>}
            </div>
            {hero.pct !== null && (
              <div className="lf-metrics__hero-meter">
                <MetricMeter pct={hero.pct} />
              </div>
            )}
          </div>

          {rest.length > 0 && (
            <div className="lf-metrics__grid">
              {rest.map((metric) => (
                <div
                  key={metric.key}
                  className="lf-metrics__tile"
                  style={{ "--lf-metric-accent": metric.accent }}
                  title={metric.hint || undefined}
                >
                  <MetricLabel metric={metric} />
                  <div className="lf-metrics__value">
                    {metric.display}
                    {metric.unit && <span className="lf-metrics__unit">{metric.unit}</span>}
                  </div>
                  {metric.pct !== null ? (
                    <MetricMeter pct={metric.pct} />
                  ) : (
                    <span className="lf-metrics__note">{metric.note || " "}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default TrainingMetricsPanel;
